import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAtom } from 'jotai'
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  ArrowBack,
  AutoFixHigh,
  ContentCopy,
  DragIndicator,
  OpenInNew,
  Publish,
  Refresh,
  Save,
  Visibility,
  VisibilityOff,
  Loop,
} from '@mui/icons-material'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { isAdminOrManager } from '../../utils/roles'
import { userAtom } from '../../state/auth'
import {
  northStarApi,
  NorthStarSectionInstance,
  NorthStarSectionTemplate,
  NorthStarRecord,
  NorthStarViewRow,
} from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import FormRenderer from '../../components/north-star/FormRenderer'
import PageHelpButton from '../../components/PageHelpButton'
import { MissingResp, missingFromRejection } from '../../components/north-star/missingData'

// ----- Sortable section row in the editor's left list -----

interface SortableSectionRowProps {
  section: NorthStarSectionInstance
  template: NorthStarSectionTemplate | undefined
  isActive: boolean
  onSelect: () => void
  onToggleEnabled: () => void
}

function SortableSectionRow({
  section,
  template,
  isActive,
  onSelect,
  onToggleEnabled,
}: SortableSectionRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.sectionTypeId,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : section.enabled ? 1 : 0.5,
    boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.12)' : undefined,
  }

  return (
    <Stack
      ref={setNodeRef}
      direction="row"
      alignItems="center"
      spacing={0.5}
      style={style}
      sx={{
        px: 0.5,
        py: 0.75,
        borderRadius: 1,
        cursor: 'pointer',
        background: isActive ? 'action.selected' : 'transparent',
        '&:hover': { background: 'action.hover' },
      }}
      onClick={onSelect}
    >
      <Box
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          cursor: isDragging ? 'grabbing' : 'grab',
          px: 0.25,
        }}
        aria-label="Drag to reorder"
      >
        <DragIndicator fontSize="small" />
      </Box>
      <Typography variant="body2" sx={{ flex: 1, fontWeight: isActive ? 600 : 400 }}>
        {template?.name || section.sectionTypeId}
      </Typography>
      <Switch
        size="small"
        checked={section.enabled}
        onClick={(e) => e.stopPropagation()}
        onChange={onToggleEnabled}
      />
    </Stack>
  )
}

export default function NorthStarEditor() {
  const navigate = useNavigate()
  const { id: clientId = '' } = useParams<{ id: string }>()
  const [user] = useAtom(userAtom)
  const { showSuccess, showError } = useNotification()

  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [record, setRecord] = useState<NorthStarRecord | null>(null)
  const [templates, setTemplates] = useState<Record<string, NorthStarSectionTemplate>>({})
  const [sections, setSections] = useState<NorthStarSectionInstance[]>([])
  const [meta, setMeta] = useState<NorthStarRecord['meta']>(undefined)
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null)
  const [missing, setMissing] = useState<MissingResp | null>(null)
  const [views, setViews] = useState<NorthStarViewRow[]>([])
  const [snackOpen, setSnackOpen] = useState(false)

  const canEdit = isAdminOrManager(user)

  useEffect(() => {
    if (!canEdit || !clientId) return
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, canEdit])

  const load = async () => {
    try {
      setLoading(true)
      const resp = await northStarApi.get(clientId)
      // resp is null if no draft exists yet
      if (resp) {
        setRecord(resp.northStar)
        setTemplates(resp.templates || {})
        if (resp.northStar) {
          setSections(resp.northStar.sections || [])
          setMeta(resp.northStar.meta)
        } else {
          setSections([])
          setMeta(undefined)
        }
        setViews(resp.recentViews || [])
        const first = (resp.northStar?.sections || []).find((s) => s.enabled !== false)
        setActiveSectionId(first?.sectionTypeId ?? null)
      }
    } catch (err) {
      console.error(err)
      showError("Couldn't load the North Star. Try refreshing the page.")
    } finally {
      setLoading(false)
    }
  }

  const pollUntilReady = async () => {
    // Poll every 5 seconds for up to 3 minutes
    const maxAttempts = 36
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((r) => setTimeout(r, 5000))
      try {
        const resp = await northStarApi.get(clientId)
        if (!resp || !resp.northStar) continue
        const status = resp.northStar.status
        if (status === 'ready') {
          setRecord(resp.northStar)
          setTemplates(resp.templates || {})
          setSections(resp.northStar.sections || [])
          setMeta(resp.northStar.meta)
          if (resp.northStar.sections.length > 0) {
            setActiveSectionId(resp.northStar.sections[0].sectionTypeId)
          }
          setBusy(null)
          showSuccess('Draft generated — review and save')
          return
        }
        if (status === 'error') {
          setBusy(null)
          showError(resp.northStar.errorMessage || 'Generation failed')
          return
        }
        // status === 'generating' — keep polling
      } catch (err) {
        console.error('Poll error', err)
      }
    }
    setBusy(null)
    showError('Generation took too long. Contact your platform administrator.')
  }

  const handleGenerate = async () => {
    try {
      setBusy('generate')
      setMissing(null)
      await northStarApi.generate(clientId)
      // generate returns immediately with status: 'generating'.
      // Start polling — handlers above will clear busy when status becomes 'ready' or 'error'.
      pollUntilReady()
    } catch (err) {
      const apiErr = err as { status?: number; message?: string; details?: Partial<MissingResp> }
      setBusy(null)
      if (apiErr?.status === 422) {
        setMissing(missingFromRejection(apiErr))
      } else {
        console.error(err)
        showError(apiErr?.message || 'Generation failed')
      }
    }
  }

  const handleSave = async () => {
    try {
      setBusy('save')
      const saved = await northStarApi.save({ clientId, sections, meta })
      setRecord(saved)
      showSuccess(`Saved (v${saved.version})`)
    } catch (err) {
      console.error(err)
      showError('Save failed')
    } finally {
      setBusy(null)
    }
  }

  const handlePublish = async () => {
    try {
      setBusy('publish')
      const result = await northStarApi.publish({ clientId })
      setRecord((prev) =>
        prev ? { ...prev, ...result } : ({ ...result, clientId, version: 0, sections: [], isPublished: !!result.isPublished } as NorthStarRecord)
      )
      showSuccess(`Published — share https://${window.location.host}/north-star/${result.slug}`)
    } catch (err) {
      console.error(err)
      showError('Publish failed')
    } finally {
      setBusy(null)
    }
  }

  const handleUnpublish = async () => {
    try {
      setBusy('publish')
      const result = await northStarApi.publish({ clientId, unpublish: true })
      setRecord((prev) => (prev ? { ...prev, ...result } : prev))
      showSuccess('Unpublished')
    } catch (err) {
      console.error(err)
      showError('Unpublish failed')
    } finally {
      setBusy(null)
    }
  }

  const handleRotate = async () => {
    if (!window.confirm('Rotate the share URL? The old link will stop working.')) return
    try {
      setBusy('publish')
      const result = await northStarApi.publish({ clientId, rotateSlug: true })
      setRecord((prev) => (prev ? { ...prev, ...result } : prev))
      showSuccess('URL rotated')
    } catch (err) {
      console.error(err)
      showError('Rotate failed')
    } finally {
      setBusy(null)
    }
  }

  const updateSectionValues = (sectionTypeId: string, next: Record<string, unknown>) => {
    setSections((prev) =>
      prev.map((s) => (s.sectionTypeId === sectionTypeId ? { ...s, fieldValues: next } : s))
    )
  }

  const updateSectionMeta = (
    sectionTypeId: string,
    patch: Partial<Pick<NorthStarSectionInstance, 'titleOverride' | 'descriptionOverride'>>
  ) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.sectionTypeId !== sectionTypeId) return s
        const next = { ...s, ...patch }
        // Treat empty strings as "no override" so blank = fall back to template.
        if (patch.titleOverride !== undefined && !patch.titleOverride.trim()) {
          delete next.titleOverride
        }
        if (patch.descriptionOverride !== undefined && !patch.descriptionOverride.trim()) {
          delete next.descriptionOverride
        }
        return next
      })
    )
  }

  const toggleSection = (sectionTypeId: string) => {
    setSections((prev) =>
      prev.map((s) => (s.sectionTypeId === sectionTypeId ? { ...s, enabled: !s.enabled } : s))
    )
  }

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = sections.findIndex((s) => s.sectionTypeId === active.id)
    const newIndex = sections.findIndex((s) => s.sectionTypeId === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    const reordered = arrayMove(sections, oldIndex, newIndex)
    reordered.forEach((s, i) => (s.order = i))
    setSections(reordered)
  }

  const activeSection = useMemo(
    () => sections.find((s) => s.sectionTypeId === activeSectionId) || null,
    [sections, activeSectionId]
  )
  const activeTemplate = activeSection ? templates[activeSection.sectionTypeId] : null

  const shareUrl = record?.slug ? `${window.location.origin}/north-star/${record.slug}` : null

  if (!canEdit) {
    return (
      <Box p={3}>
        <Alert severity="error">Admins and managers only.</Alert>
      </Box>
    )
  }

  return (
    <Box>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: '#fff',
          color: '#1a1a1a',
          borderBottom: 1,
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.appBar + 1,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => navigate(`/clients/${clientId}`)}
            aria-label="Back"
            sx={{ color: '#1a1a1a' }}
          >
            <ArrowBack />
          </IconButton>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, ml: 1 }}>
            <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
              North Star
            </Typography>
            <PageHelpButton articleId="page-north-star-editor" />
            {record?.isPublished ? (
              <Chip label="Published" color="success" size="small" />
            ) : (
              record?.version ? <Chip label={`Draft v${record.version}`} size="small" /> : null
            )}
          </Stack>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Reload">
              <IconButton onClick={load} disabled={!!busy} sx={{ color: '#1a1a1a' }}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              startIcon={busy === 'generate' ? <CircularProgress size={16} /> : <AutoFixHigh />}
              onClick={handleGenerate}
              disabled={!!busy}
            >
              {sections.length > 0 ? 'Regenerate' : 'Generate draft'}
            </Button>
            <Button
              variant="contained"
              startIcon={busy === 'save' ? <CircularProgress size={16} /> : <Save />}
              onClick={handleSave}
              disabled={!!busy || sections.length === 0}
            >
              Save
            </Button>
            {record?.isPublished ? (
              <Button
                color="warning"
                startIcon={<VisibilityOff />}
                onClick={handleUnpublish}
                disabled={!!busy}
              >
                Unpublish
              </Button>
            ) : (
              <Button
                color="success"
                startIcon={<Publish />}
                onClick={handlePublish}
                disabled={!!busy || sections.length === 0}
              >
                Publish
              </Button>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      {missing && (
        <Alert severity="error" sx={{ m: 3 }} onClose={() => setMissing(null)}>
          <Typography sx={{ fontWeight: 600 }}>{missing.message}</Typography>
          {missing.missing.length > 0 && missing.links && (
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              {missing.missing.map((m) => {
                const link = missing.links?.[m]
                if (!link) return null
                const label =
                  ({
                    dream_job: 'Open Dream Job',
                    clifton_strengths: 'Open Strengths',
                    resume_content: 'Open Resume',
                  } as Record<string, string>)[m] || `Open ${m.replace('_', ' ')}`
                return (
                  <MuiLink key={m} href={link} target="_blank" rel="noreferrer">
                    {label}
                  </MuiLink>
                )
              })}
            </Stack>
          )}
        </Alert>
      )}

      {shareUrl && record?.isPublished && (
        <Alert
          severity="success"
          sx={{ m: 3 }}
          action={
            <Stack direction="row" spacing={1}>
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl)
                    setSnackOpen(true)
                  }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open">
                <IconButton size="small" color="inherit" href={shareUrl} target="_blank">
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rotate URL">
                <IconButton size="small" color="inherit" onClick={handleRotate} disabled={!!busy}>
                  <Loop fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        >
          Live at <strong>{shareUrl}</strong>
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      ) : sections.length === 0 ? (
        <Box p={3}>
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Stack spacing={2} alignItems="center">
              <Typography variant="h6">No North Star draft yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
                Click "Generate draft" to have Claude assemble a first-pass strategy from this
                client's dream-job target, Clifton strengths, and generated resume. You'll review and edit
                each section before publishing.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AutoFixHigh />}
                onClick={handleGenerate}
                disabled={!!busy}
              >
                Generate draft
              </Button>
            </Stack>
          </Paper>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 2, p: 3 }}>
          {/* Section list */}
          <Paper variant="outlined" sx={{ p: 1.5, height: 'fit-content', position: 'sticky', top: 80 }}>
            <Typography variant="overline" sx={{ px: 1 }}>
              Sections
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block', mb: 1 }}>
              Drag to reorder · toggle to show/hide
            </Typography>
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleSectionDragEnd}
            >
              <SortableContext
                items={sections.map((s) => s.sectionTypeId)}
                strategy={verticalListSortingStrategy}
              >
                <Stack spacing={0.5}>
                  {sections.map((s) => (
                    <SortableSectionRow
                      key={s.sectionTypeId}
                      section={s}
                      template={templates[s.sectionTypeId]}
                      isActive={s.sectionTypeId === activeSectionId}
                      onSelect={() => setActiveSectionId(s.sectionTypeId)}
                      onToggleEnabled={() => toggleSection(s.sectionTypeId)}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
            {record?.slug && (
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="overline">Views</Typography>
                <Typography variant="h6">{views.length}</Typography>
                {views.slice(0, 5).map((v) => (
                  <Typography key={v.viewedAt} variant="caption" display="block" color="text.secondary">
                    {new Date(v.viewedAt).toLocaleString()} · {v.truncatedIp || '?'}
                  </Typography>
                ))}
                {meta?.generatedAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Generated {new Date(meta.generatedAt).toLocaleString()}
                  </Typography>
                )}
              </Box>
            )}
            {record?.slug && (
              <Box sx={{ mt: 2 }}>
                <Button
                  size="small"
                  startIcon={<Visibility />}
                  href={`/north-star/${record.slug}?preview=1`}
                  target="_blank"
                >
                  Preview
                </Button>
              </Box>
            )}
          </Paper>

          {/* Editor pane */}
          <Box>
            {activeSection && activeTemplate ? (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack spacing={1} mb={3}>
                  <Typography variant="overline">{activeTemplate.renderType}</Typography>
                  <Typography variant="h5">{activeTemplate.name}</Typography>
                  {activeTemplate.description && (
                    <Typography variant="body2" color="text.secondary">
                      {activeTemplate.description}
                    </Typography>
                  )}
                </Stack>

                {/* Per-client overrides of the section heading + sub-description shown on the public page.
                    Blank = use the template defaults. Edits here only affect THIS client. */}
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    background: 'rgba(0,0,0,0.015)',
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 2, fontSize: 10 }}
                  >
                    Section Heading (shown on the public page)
                  </Typography>
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    <TextField
                      label="Heading"
                      size="small"
                      fullWidth
                      value={activeSection.titleOverride ?? ''}
                      onChange={(e) =>
                        updateSectionMeta(activeSection.sectionTypeId, {
                          titleOverride: e.target.value,
                        })
                      }
                      placeholder={activeTemplate.name}
                      helperText={
                        activeSection.titleOverride
                          ? `Overrides the template name "${activeTemplate.name}" for this client only`
                          : `Blank = uses the template name "${activeTemplate.name}"`
                      }
                    />
                    <TextField
                      label="Sub-description"
                      size="small"
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={3}
                      value={activeSection.descriptionOverride ?? ''}
                      onChange={(e) =>
                        updateSectionMeta(activeSection.sectionTypeId, {
                          descriptionOverride: e.target.value,
                        })
                      }
                      placeholder={activeTemplate.description || ''}
                      helperText={
                        activeSection.descriptionOverride
                          ? `Overrides the template description for this client only`
                          : `Blank = uses the template description${activeTemplate.description ? ` "${activeTemplate.description}"` : ''}`
                      }
                    />
                  </Stack>
                </Box>

                <FormRenderer
                  fields={activeTemplate.fields || []}
                  values={activeSection.fieldValues || {}}
                  onChange={(next) => updateSectionValues(activeSection.sectionTypeId, next)}
                />
              </Paper>
            ) : activeSection ? (
              <Alert severity="warning">
                Template "{activeSection.sectionTypeId}" not found. It may have been removed by an
                admin. Disable this section or restore the template.
              </Alert>
            ) : (
              <Alert severity="info">Select a section on the left to edit.</Alert>
            )}
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={2000}
        onClose={() => setSnackOpen(false)}
        message="Share URL copied"
      />
    </Box>
  )
}
